import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { NSSelect } from 'bricks';
import { customStyles } from 'bricks/NSSelect/NSSelect';
import { IFormattedCashFlowPeriod } from 'views/ProFormaTable/types';
import { PlaceholderProps, components } from 'react-select';

export interface IPeriodsSelectProps {
    handleChange: (value: string) => void;
    disabled?: boolean;
    hasInvertedColors?: boolean;
    compact?: boolean;
    alignRight?: boolean;
    showCaretOnHover?: boolean;
    options: IFormattedCashFlowPeriod[];
    value: string | null;
}

// all local date formatting is done assuming dates are passed in UTC
const PeriodsSelect = ({
    handleChange,
    disabled,
    hasInvertedColors = false,
    compact = false,
    alignRight = false,
    showCaretOnHover = false,
    options,
    value,
}: IPeriodsSelectProps) => {
    const [selectedValue, setSelectedValue] = useState<string | null>(value || null);
    const [isHovered, setIsHovered] = useState(false);
    const hideDropdownIndicator = compact || (showCaretOnHover && !isHovered);

    const CustomPlaceholder: React.FC<PlaceholderProps> = props => (
        <components.Placeholder {...props}>
            <span className="text-primary text-nowrap font-italic">
                + Add date
            </span>
        </components.Placeholder>
    );

    const getDropdownIndicatorColor = (isFocused: boolean) => {
        if (isFocused) return '#F1F1F1';
        return hasInvertedColors ? '#464e5b' : '#30343e';
    };

    useEffect(() => {
        if (value !== selectedValue) {
            setSelectedValue(value || null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const selectEl = (
        <NSSelect
            name="periodsSelect"
            components={{
                Placeholder: CustomPlaceholder,
                ...(hideDropdownIndicator && { DropdownIndicator: () => null }),
                ...(showCaretOnHover && { IndicatorSeparator: () => null }),
            }}
            options={options}
            value={options.find(option => option.value === selectedValue)}
            onChange={option => handleChange(option.value)}
            isClearable={false}
            menuPortalTarget={document.body}
            menuPosition="absolute"
            isDisabled={disabled}
            filterOption={(option: unknown, rawInput) => {
                const currentOption = option as IFormattedCashFlowPeriod;
                const targetPeriod = options.find(period => period.value === currentOption.value);
                let monthLabel = '';
                if (targetPeriod) {
                    monthLabel = targetPeriod.period.toLowerCase();
                }

                return (
                    currentOption.label.toLowerCase().includes(rawInput.toLowerCase())
                    || monthLabel.includes(rawInput.toLowerCase())
                );
            }}
            formatOptionLabel={(option: unknown, { context }: { context: string }) => {
                const currentOption = option as IFormattedCashFlowPeriod;

                if (context !== 'menu' && context !== 'value') {
                    return currentOption.label;
                }

                return (
                    <div className={classNames('d-flex flex-column', {
                        'py-0': context === 'value',
                        'align-items-end text-right': alignRight,
                    })}
                    >
                        <small>{currentOption.period}</small>
                        <div className={classNames({ 'text-primary': context === 'value' })}>{currentOption.label}</div>
                    </div>
                );
            }}
            styles={{
                ...customStyles,
                control: provided => ({
                    ...provided,
                    color: '#aab8c5',
                    backgroundColor: hasInvertedColors ? '#464e5b' : '#30343e',
                    borderRadius: 4,
                    cursor: 'pointer',
                    border: 'none',
                    height: compact ? '28px' : '42px',
                    padding: compact ? '0 0' : undefined,
                    '&:hover': {
                        border: 'none',
                        backgroundColor: hasInvertedColors ? '#30343e' : '#464e5b',
                    },
                }),
                valueContainer: provided => ({
                    ...provided,
                    padding: compact ? '0 4px' : provided.padding,
                    justifyContent: alignRight ? 'flex-end' : 'flex-start',
                }),
                singleValue: provided => ({
                    ...provided,
                    color: '#e3eaef',
                    margin: compact ? '0' : provided.margin,
                    position: 'relative',
                    transform: 'none',
                    maxWidth: '100%',
                    textAlign: alignRight ? 'right' : 'left',
                }),
                dropdownIndicator: (provided, state) => ({
                    ...provided,
                    color: getDropdownIndicatorColor(state.isFocused),

                    '&:hover': {
                        color: hasInvertedColors ? '#F1F1F1' : '#30343e',
                        backgroundColor: hasInvertedColors ? '#30343e' : '#464e5b',
                    },
                }),
                menuPortal: base => ({
                    ...base,
                    zIndex: 9999,
                    width: alignRight ? base.width : '120px',
                }),
            }}
        />
    );

    if (!showCaretOnHover) return selectEl;

    return (
        <div
            className="w-100"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {selectEl}
        </div>
    );
};

export default PeriodsSelect;
